import React, { useState, useMemo, useRef } from 'react';
import { TableOfContentV2Styles as classes } from './TableOfContentsV2Styles';
import { useTranslation } from '@stateManagement/TranslationContext';
import { getTranslation } from '@helpers/languageTranslations';
import { Grid } from '@mui/material';
import { DropdownIcon } from '@components/IconComponents/DropdownIcon';
import { getHeadingsFromContent } from './helperTableOfContents';
import { Heading, v2Colors, useAccessiblePopout, HeadingVariant } from '@web-for-marketing/react-ui';
import { useTableOfContents } from '@hooks/useTableOfContents';
import { TableOfContentsMobileItems } from './TableOfContentsMobileItems';

interface TableOfContentsProps {
    content: string;
    id?: string;
    headingLevel?: HeadingVariant;
    className?: string;
}

export const TableOfContentsMobile = ({
    content,
    id,
    headingLevel = 'h2',
    className,
}: TableOfContentsProps): JSX.Element | null => {
    const { nestedHeadingsH2H3, nestedHeadingsH3 } = useMemo(() => getHeadingsFromContent(content), [content]);
    const { selectedLanguageTranslation } = useTranslation();
    const tableOfContentTranslation = useMemo(() => {
        return getTranslation(selectedLanguageTranslation, 'blogTableContent');
    }, [selectedLanguageTranslation]);
    const { activeHeader } = useTableOfContents(content);
    const [open, setOpen] = useState(false);
    const close = (): void => setOpen(false);
    const popover = useRef(null);

    useAccessiblePopout(popover, open, close, {
        disableScroll: false,
    });

    const removeFocusAfterScroll = (): void => {
        document.addEventListener('scroll', () => {
            (document?.activeElement as HTMLElement)?.blur();
        });
    };

    const scrollToElement = (id: string, event: React.MouseEvent<HTMLElement>): void => {
        event.preventDefault();
        const element = document.querySelector(`#${id}`);
        if (element) {
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.scrollY - 50;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth',
            });
        }
    };

    function handleItemSelect(id: string, event: React.MouseEvent<HTMLElement>): void {
        scrollToElement(id, event);
        removeFocusAfterScroll();
        close();
    }

    if (nestedHeadingsH2H3.length > 0 || nestedHeadingsH3.length > 0) {
        return (
            <div css={classes.mobileContainer} className={className}>
                <Grid ref={popover}>
                    <Grid
                        component='button'
                        css={classes.mobileTitleButton}
                        onClick={() => setOpen((open) => !open)}
                        aria-label={open ? 'Click to close table of contents' : 'Click to open table of contents'}
                    >
                        <Heading id='menu-content-title' variant='h4' component={headingLevel} color='activePrimary'>
                            {tableOfContentTranslation.title}
                        </Heading>
                        <DropdownIcon
                            height='2.5rem'
                            width='2.5rem'
                            color={v2Colors.text.activePrimary}
                            css={open ? classes.rotate : ''}
                        />
                    </Grid>
                    <Grid
                        container
                        css={[classes.popover, open ? classes.showOpen : '']}
                        aria-hidden={!open}
                        role='dialog'
                        ref={popover}
                        id={id}
                        aria-labelledby='menu-content-title'
                    >
                        <Grid container component='nav' aria-label='Content list'>
                            <ul css={classes.listUlMobile}>
                                <TableOfContentsMobileItems
                                    items={nestedHeadingsH2H3.length > 0 ? nestedHeadingsH2H3 : nestedHeadingsH3}
                                    activeHeader={activeHeader}
                                    onSelectItem={handleItemSelect}
                                />
                            </ul>
                        </Grid>
                    </Grid>
                </Grid>
                {open ? <div aria-hidden onClick={close} css={classes.background} /> : null}
            </div>
        );
    } else {
        return null;
    }
};
